/**
 * API Host Whitelist Configuration
 *
 * This module provides host validation to prevent information leakage vulnerabilities
 * by ensuring all API requests are only sent to approved, whitelisted hosts.
 *
 * Security: Veracode Remediation - Information Leakage Prevention
 */

import {safeConsoleError, safeConsoleWarn} from './sanitize';

/**
 * Parse allowed hosts from environment variables
 */
function parseAllowedHosts(): string[] {
    const apiBase = process.env.REACT_APP_API_BASE;

    if (!apiBase) {
        throw new Error('API host whitelist is not configured. Please set REACT_APP_API_BASE environment variable.');
    }

    // Security: Always use HTTPS for all environments
    const protocol = 'https://';

    // Construct primary API URL
    const primaryApiHost = protocol + apiBase;

    // Start with primary API host
    const hosts = [primaryApiHost];

    // Add any additional hosts if configured
    const additionalHosts = process.env.REACT_APP_ADDITIONAL_API_HOSTS;
    if (additionalHosts) {
        const parsed = additionalHosts
            .split(',')
            .map(host => host.trim().replace(/\/+$/, '')) // Remove trailing slashes
            .filter(host => host.length > 0); // Remove empty strings

        hosts.push(...parsed);
    }
    return hosts;
}

const APPROVED_HOSTS = parseAllowedHosts();

/**
 * Custom error class for host validation failures
 */
export class HostNotWhitelistedError extends Error {
    constructor(host: string) {
        super(`Security Error: Host "${host}" is not in the approved whitelist`);
        this.name = 'HostNotWhitelistedError';

        // Log security violation using safe wrappers to prevent log forging attacks
        // These wrappers automatically sanitize untrusted input before logging
        if (process.env.NODE_ENV === 'production') {
            safeConsoleError('[SECURITY] Attempt to make request to non-whitelisted host:', host);
        } else {
            safeConsoleWarn('[SECURITY] Blocked request to non-whitelisted host:', host);
        }
    }
}

/**
 * Validates that a given host is in the approved whitelist
 *
 * @param host - The host URL to validate (with protocol)
 * @returns The validated host if approved
 * @throws {HostNotWhitelistedError} If the host is not whitelisted
 */
export function validateHost(host: string): typeof APPROVED_HOSTS[number] {
    // Normalize the host by trimming whitespace and removing trailing slashes
    host = host.trim().replace(/\/+$/, '');

    let normalizedHost = host;

    // Add https:// only if no protocol is present
    if (!normalizedHost.match(/^[a-z][a-z0-9+.-]*:\/\//i)) {
        normalizedHost = `https://${normalizedHost}`;
    }

    // Check if the host is in the approved list
    if (APPROVED_HOSTS.includes(normalizedHost as any)) {
        return host as any;
    }

    // Host is not whitelisted - throw security error
    throw new HostNotWhitelistedError(normalizedHost);
}

/**
 * Custom error class for path validation failures
 */
export class InvalidPathError extends Error {
    constructor(path: string, reason: string) {
        super(`Security Error: Invalid path "${path}" - ${reason}`);
        this.name = 'InvalidPathError';

        // Log security violation using safe wrappers to prevent log forging attacks
        // These wrappers automatically sanitize untrusted input before logging
        if (process.env.NODE_ENV === 'production') {
            safeConsoleError('[SECURITY] Attempt to use invalid path:', path, 'Reason:', reason);
        } else {
            safeConsoleWarn('[SECURITY] Blocked invalid path:', path, 'Reason:', reason);
        }
    }
}

/**
 * Validates that a given path is safe to use in API requests
 * Prevents SSRF attacks by ensuring paths cannot override the host or protocol
 *
 * @param path - The URL path to validate
 * @returns The validated path
 * @throws {InvalidPathError} If the path contains unsafe patterns
 */
export function validatePath(path: string): string {
    // Ensure path is a string
    if (typeof path !== 'string') {
        throw new InvalidPathError(String(path), 'Path must be a string');
    }

    // Trim the path
    const trimmedPath = path.trim();

    // Path must start with /
    if (!trimmedPath.startsWith('/')) {
        throw new InvalidPathError(path, 'Path must start with /');
    }

    // Prevent protocol override attempts (http://, https://, ftp://, etc.)
    if (trimmedPath.match(/^\/+[a-z][a-z0-9+.-]*:\/\//i)) {
        throw new InvalidPathError(path, 'Path contains protocol separator');
    }

    // Prevent host override attempts using // (protocol-relative URLs)
    if (trimmedPath.match(/^\/\/+[^/]/)) {
        throw new InvalidPathError(path, 'Path attempts to override host with //');
    }

    // Prevent @ symbol which can be used in URLs to override authority
    if (trimmedPath.includes('@')) {
        throw new InvalidPathError(path, 'Path contains @ symbol');
    }

    // Prevent backslashes which can be confused with forward slashes in some contexts
    if (trimmedPath.includes('\\')) {
        throw new InvalidPathError(path, 'Path contains backslash');
    }

    // Prevent null bytes
    if (trimmedPath.includes('\0')) {
        throw new InvalidPathError(path, 'Path contains null byte');
    }

    // Prevent newlines and carriage returns (CRLF injection)
    if (trimmedPath.match(/[\r\n]/)) {
        throw new InvalidPathError(path, 'Path contains newline characters');
    }

    // Prevent directory traversal attempts
    if (trimmedPath.includes('..')) {
        throw new InvalidPathError(path, 'Path contains directory traversal sequence');
    }

    // Additional check: ensure the path doesn't try to escape using URL encoding
    // Decode once and check if it would fail any of the above checks
    try {
        const decoded = decodeURIComponent(trimmedPath);
        if (decoded !== trimmedPath) {
            // If decoding changed the path, validate the decoded version too
            if (decoded.includes('@') || decoded.includes('\\') || decoded.includes('..') || decoded.match(/[\r\n\0]/)) {
                throw new InvalidPathError(path, 'Path contains encoded forbidden characters');
            }
            if (decoded.match(/^\/+[a-z][a-z0-9+.-]*:\/\//i)) {
                throw new InvalidPathError(path, 'Path contains encoded protocol separator');
            }
            // Check for double-slash pattern after decoding (protocol-relative URLs)
            if (decoded.match(/^\/\/+/)) {
                throw new InvalidPathError(path, 'Path contains encoded host override attempt');
            }
        }
    } catch (e) {
        // Re-throw if it's our own error
        if (e instanceof InvalidPathError) {
            throw e;
        }
        // If decoding fails for other reasons (malformed encoding), it's probably okay
        // The browser's fetch will handle malformed URLs
    }

    return trimmedPath;
}

/**
 * Get all approved hosts (useful for debugging/configuration)
 */
export function getApprovedHosts() {
    return APPROVED_HOSTS;
}
